import React, { useState, useEffect, useCallback } from 'react';
import { scheduledReportsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { ScheduleReportModal } from '../components/ScheduleReportModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { CalendarClock, MoreVertical, Play, Pause, PlayCircle, Trash2, Edit, History, Loader2, Clock, Mail, AlertTriangle, Copy, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const FREQ_LABELS = { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', quarterly: 'Quarterly', biannual: 'Biannual', annual: 'Annual' };

function RunHistoryDialog({ open, onOpenChange, scheduleId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !scheduleId) return;
    setLoading(true);
    scheduledReportsAPI.getRuns(scheduleId)
      .then(res => setRuns(res.data || []))
      .catch(() => toast.error('Failed to load run history'))
      .finally(() => setLoading(false));
  }, [open, scheduleId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" />Run History</DialogTitle>
          <DialogDescription>Last 20 executions</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : runs.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No runs yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Finished</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.run_date_local}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === 'sent' ? 'default' : 'destructive'} className={r.status === 'sent' ? 'bg-emerald-500/15 text-emerald-700 border-emerald-200' : ''}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.sent_to_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.run_started_at ? new Date(r.run_started_at).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.run_finished_at ? new Date(r.run_finished_at).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate">{r.error || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ScheduledReports() {
  const { user, hasPerm } = useAuth();
  const canWrite = user?.role === 'owner' || user?.role === 'admin';

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [editSchedule, setEditSchedule] = useState(null);

  const loadSchedules = useCallback(async () => {
    try {
      const { data } = await scheduledReportsAPI.list();
      setSchedules(data || []);
    } catch {
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const handleToggle = async (sched) => {
    setTogglingId(sched.id);
    try {
      const { data } = await scheduledReportsAPI.toggle(sched.id);
      setSchedules(prev => prev.map(s => s.id === sched.id ? data : s));
      toast.success(data.is_active ? 'Schedule resumed' : 'Schedule paused');
    } catch {
      toast.error('Failed to toggle schedule');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRunNow = async (sched) => {
    setRunningId(sched.id);
    try {
      const { data } = await scheduledReportsAPI.runNow(sched.id);
      if (data.schedule) {
        setSchedules(prev => prev.map(s => s.id === sched.id ? data.schedule : s));
      }
      const run = data.run;
      if (run?.status === 'sent') {
        toast.success(`Sent to ${run.sent_to_count} recipient(s)`);
      } else {
        toast.error(run?.error || 'Run failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run');
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await scheduledReportsAPI.remove(deleteConfirm.id);
      setSchedules(prev => prev.filter(s => s.id !== deleteConfirm.id));
      toast.success('Schedule deleted');
      setDeleteConfirm(null);
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditDone = (updated) => {
    setSchedules(prev => prev.map(s => s.id === updated.id ? updated : s));
    setEditSchedule(null);
  };

  const truncateEmails = (emails, max = 2) => {
    if (!emails || emails.length === 0) return '-';
    const shown = emails.slice(0, max).join(', ');
    return emails.length > max ? `${shown} +${emails.length - max}` : shown;
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="scheduled-reports-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading">Scheduled Reports</h1>
            <p className="text-muted-foreground mt-1 text-sm">Automated recurring report delivery</p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              Active Schedules ({schedules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarClock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No scheduled reports yet.</p>
                <p className="text-sm mt-1">Go to Reports, run a report, then click "Schedule" to set up recurring delivery.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Next Run</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Status</TableHead>
                      {canWrite && <TableHead className="w-[50px]" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map(sched => (
                      <TableRow key={sched.id} data-testid={`schedule-row-${sched.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{sched.report_name}</p>
                            <p className="text-xs text-muted-foreground">{sched.report_type}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{FREQ_LABELS[sched.frequency_type] || sched.frequency_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            {sched.next_run_local || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {sched.last_run_at_utc ? (
                            <div className="flex items-center gap-1.5 text-sm">
                              {sched.last_run_status === 'sent' ? (
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              ) : sched.last_run_status === 'failed' ? (
                                <XCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
                              ) : (
                                <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              )}
                              <span className={sched.last_run_status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}>
                                {formatDistanceToNow(new Date(sched.last_run_at_utc + 'Z'), { addSuffix: true })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Never</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <TooltipProvider delayDuration={200}>
                            <ShadcnTooltip>
                              <TooltipTrigger asChild>
                                <button
                                  className="text-sm text-left flex items-center gap-1 hover:text-foreground transition-colors group"
                                  onClick={() => {
                                    const text = (sched.recipient_emails || []).join(', ');
                                    navigator.clipboard.writeText(text);
                                    toast.success('Recipients copied');
                                  }}
                                  data-testid={`copy-recipients-${sched.id}`}
                                >
                                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                                  <span>{truncateEmails(sched.recipient_emails)}</span>
                                  <Copy className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-sm">
                                <p className="text-xs font-medium mb-1">Click to copy all</p>
                                <p className="text-xs">{(sched.recipient_emails || []).join(', ')}</p>
                              </TooltipContent>
                            </ShadcnTooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          {sched.is_active ? (
                            <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-200">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Paused</Badge>
                          )}
                        </TableCell>
                        {canWrite && (
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`schedule-actions-${sched.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleRunNow(sched)} disabled={runningId === sched.id}>
                                  {runningId === sched.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                                  Run Now
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setEditSchedule(sched)}>
                                  <Edit className="w-4 h-4 mr-2" />Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggle(sched)} disabled={togglingId === sched.id}>
                                  {sched.is_active ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                                  {sched.is_active ? 'Pause' : 'Resume'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setHistoryId(sched.id)}>
                                  <History className="w-4 h-4 mr-2" />View Runs
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setDeleteConfirm(sched)} className="text-destructive focus:text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" />Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />Delete Schedule?</DialogTitle>
            <DialogDescription>Delete "{deleteConfirm?.report_name}"? All run history will also be removed. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-schedule">
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run history */}
      <RunHistoryDialog open={!!historyId} onOpenChange={(o) => !o && setHistoryId(null)} scheduleId={historyId} />

      {/* Edit modal */}
      {editSchedule && (
        <ScheduleReportModal
          open={!!editSchedule}
          onOpenChange={(o) => !o && setEditSchedule(null)}
          reportType={editSchedule.report_type}
          reportName={editSchedule.report_name}
          currentFilters={editSchedule.report_config_json?.filters}
          dateRangeType={editSchedule.report_config_json?.date_range_type}
          existingSchedule={editSchedule}
          onCreated={handleEditDone}
        />
      )}
    </Layout>
  );
}
